import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
  getProjectRounds as getProjectRoundsApi,
  fetchPhotosByRound as fetchPhotosByRoundApi,
  getRoundsBySpace,
  getOccupancyAndUtilizationSummaries as getSpaceTypeRoundsApi,
} from 'spw-api';
import { camelize } from 'spw-shared/utils';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import {
  actions as roundsActions,
  FETCH_ROUND_PHOTOS,
  FETCH_SPACE_ROUNDS,
  FETCH_PROJECT_ROUNDS,
  GET_OCCUPANCY_AND_UTILIZATION_SUMMARIES,
} from './actions';
import {
  IProjectRound,
  IRoundBuilding,
  IRoundFloor,
  ISpaceRound,
  OccupancyAndUtilizationSummaries,
  RoundVM,
} from './types';
import { IPhoto } from '../photo/types';
import { currentProject as selectCurrentProject } from '../projects/selectors';
import { IProject } from '../projects/types';

const generateReducedRounds = (projectRounds: IProjectRound[]): RoundVM[] => {
  const copyOfProjectRounds = JSON.parse(
    JSON.stringify(projectRounds)
  ) as IProjectRound[];

  return copyOfProjectRounds.map(
    ({
      round,
      completedSpaces,
      totalSpaces,
      numberOfBuildingsRelatedToRound,
      numberOfFloorsRelatedToRound,
    }: IProjectRound) => {
      const buildings: IRoundBuilding[] = [];

      round.buildings.forEach((building: IRoundBuilding) => {
        let buildingSpacesAvailable = 0;
        let buildingSpacesCompleted = 0;

        const floors = building.floors
          .map((floor: IRoundFloor) => {
            const {
              totalSpaces: floorTotalSpaces,
              completedSpaces: floorCompletedSpaces,
            } = floor;
            buildingSpacesAvailable += floorTotalSpaces;
            buildingSpacesCompleted += floorCompletedSpaces;

            return {
              ...floor,
            };
          })
          .sort((i, j) => i.floorNumber - j.floorNumber);

        buildings.push({
          ...building,
          totalSpaces: buildingSpacesAvailable,
          completedSpaces: buildingSpacesCompleted,
          floors,
        });
      });

      return {
        ...round,
        buildings,
        totalSpaces,
        completedSpaces,
        numberOfFloors: numberOfFloorsRelatedToRound,
        numberOfBuildings: numberOfBuildingsRelatedToRound,
        categoriesRelated: {
          individualWorkspaces: round.individual,
          meetingSpaces: round.meeting,
          amenitySpaces: round.amenity,
          supportSpaces: round.support,
        },
      };
    }
  );
};

function* fetchProjectRounds(
  action: ReturnType<typeof roundsActions.fetchProjectsRounds>
) {
  try {
    const response: {
      data: { projectRounds: IProjectRound[] };
    } = yield call(getProjectRoundsApi, action.payload.projectId);

    const { projectRounds } = response.data;

    const reducedRounds = generateReducedRounds(projectRounds);
    const selectedRoundsMap = reducedRounds.reduce(
      (roundsMap, round) => ({
        ...roundsMap,
        [round.id]: round.completedSpaces > 0,
      }),
      {}
    );
    yield put(
      roundsActions.fetchProjectsRoundsSucceeded(
        reducedRounds,
        selectedRoundsMap
      )
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* fetchSpaceRounds(
  action: ReturnType<typeof roundsActions.fetchSpaceRounds>
) {
  try {
    const currentProject: IProject = yield select(selectCurrentProject);
    const response: { data: { roundsForSpace: ISpaceRound[] } } = yield call(
      getRoundsBySpace,
      currentProject.id,
      action.payload.spaceId
    );
    const { roundsForSpace } = response.data;

    yield put(roundsActions.fetchSpaceRoundsSucceeded(roundsForSpace));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getOccupancyAndUtilizationSummaries(
  action: ReturnType<typeof roundsActions.getOccupancyAndUtilizationSummaries>
) {
  try {
    const { roundIds, buildingId, floorId } = action.payload;
    const response: {
      data: {
        getOccupancyAndUtilizationSummaries: OccupancyAndUtilizationSummaries;
      };
    } = yield call(getSpaceTypeRoundsApi, roundIds, buildingId, floorId);

    const occupancyAndUtilizationSummaries =
      response.data.getOccupancyAndUtilizationSummaries;

    yield put(
      roundsActions.getOccupancyAndUtilizationSummariesSucceeded(
        occupancyAndUtilizationSummaries
      )
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* fetchPhotosforRounds(
  action: ReturnType<typeof roundsActions.fetchPhotosForRound>
) {
  try {
    const { id } = action.payload;
    const response: { data: { roundPhotos: IPhoto[] } } = yield call(
      fetchPhotosByRoundApi,
      id
    );
    yield put(
      roundsActions.populateRoundPhotos(camelize(response.data.roundPhotos))
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(FETCH_PROJECT_ROUNDS, fetchProjectRounds),
    takeLatest(FETCH_SPACE_ROUNDS, fetchSpaceRounds),
    takeLatest(
      GET_OCCUPANCY_AND_UTILIZATION_SUMMARIES,
      getOccupancyAndUtilizationSummaries
    ),
    takeLatest(FETCH_ROUND_PHOTOS, fetchPhotosforRounds),
  ]);
}

export default saga;
